// Works out what an amendment actually changed on a policy.
//
// An amendment never edits the live quotation. It builds a brand new one that
// waits in Un_Confirmed until a checker approves or rejects it, and on its own
// that quotation does not say what the member asked to change — only what the
// cover would become. Answering "what changed" means pairing it with the
// quotation it supersedes, which the record does name:
//
//   additional_details.pending_swap = {
//     old_quotation_guid,   <- the state being replaced
//     policy_guid,          <- the quotation's own policy_guid is empty until
//     old_quotation_action,    the swap is applied, so this is the policy
//     maker_user_id
//   }
//
// So the predecessor is read straight from that guid, and the customer from the
// quotation's own customer_guid — the customer backs the policy holder details,
// which schedule entries alone do not always carry. The policy is only fetched
// when one of those two is missing: as a fallback baseline (the quotation still
// attached to it) or as a source for the customer_guid.
//
// Everything below the resolution step is pure: given two quotations it
// returns the rows that differ, grouped for display. Fields are declared in
// tables rather than compared one-by-one so a new field is one line, and any
// additional_details key nobody thought to declare is still surfaced by
// diffRemainingDetails.

import {
  getCustomerInfo,
  getQuotationByGuid,
  getPolicyProcessingDcp
} from '../../../../../lib/requests.es6'
import { formatDateLocale } from '../../../../../lib/utils.es6'
import { getFullName } from '../../../../../lib/personDetails.es6'

export const NO_VALUE = '—'

// Where the guid of the superseded quotation lives. OM Malawi records it under
// additional_details.pending_swap.old_quotation_guid — the swap the amendment is
// waiting on, alongside the policy it belongs to and the maker who raised it.
// The flatter names are kept for partners whose flows write it differently.
const PREVIOUS_GUID_PATHS = [
  ['pending_swap', 'old_quotation_guid'],
  ['previous_quotation_guid'],
  ['previous_quotation'],
  ['prior_quotation_guid'],
  ['old_quotation_guid'],
  ['amended_from_quotation_guid']
]

// ---------------------------------------------------------------------------
// Readers
// ---------------------------------------------------------------------------

const details = quote =>
  quote && quote.additional_details ? quote.additional_details : {}

const entriesOf = quote =>
  quote && Array.isArray(quote.policy_schedule_entries)
    ? quote.policy_schedule_entries
    : []

// A schedule entry holds the person either nested under `dependant` or flat on
// the entry itself, depending on which endpoint returned it.
export const personOf = entry => {
  if (!entry) return {}
  return entry.dependant ? entry.dependant : entry
}

const isPrincipal = entry => {
  const relationship = personOf(entry).relationship
  return (
    typeof relationship === 'string' &&
    relationship.toLowerCase() === 'principal'
  )
}

const principalOf = quote => {
  const entries = entriesOf(quote)
  const match = entries.filter(isPrincipal)[0]
  return personOf(match || entries[0])
}

// The payer is written in three places across onboarding and amendment: a
// nested premium_payer object on the quotation, flat payer_* keys beside it,
// and a copy on the principal's schedule entry. Merge them most-specific last.
const payerOf = quote => {
  const ad = details(quote)
  const principalDetails = principalOf(quote).additional_details || {}
  const nested =
    ad.premium_payer && typeof ad.premium_payer === 'object'
      ? ad.premium_payer
      : {}
  return Object.assign({}, principalDetails, ad, nested)
}

// Mobile money details sit both beside payment_info and inside it. Read the
// nested copy last so the more specific record wins.
const mobileMoneyOf = quote => {
  const ad = details(quote)
  const info =
    ad.payment_info && typeof ad.payment_info === 'object'
      ? ad.payment_info
      : {}
  const outer =
    ad.mobile_money && typeof ad.mobile_money === 'object'
      ? ad.mobile_money
      : {}
  const inner =
    info.mobile_money && typeof info.mobile_money === 'object'
      ? info.mobile_money
      : {}
  return Object.assign({}, outer, inner)
}

const beneficiaryOf = quote => {
  const beneficiary = details(quote).beneficiary
  return beneficiary && typeof beneficiary === 'object' ? beneficiary : {}
}

export const policyNumberOf = quote => details(quote).policy_number || null

// ---------------------------------------------------------------------------
// Formatters
// ---------------------------------------------------------------------------

const yesNo = value => {
  if (value === true) return 'Yes'
  if (value === false) return 'No'
  return null
}

const asDate = value => (value ? formatDateLocale(value) || value : null)

// For comparing two names that may differ only in spacing or case.
const normalise = value =>
  String(value || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ')

const asMoney = (currency, cents) => {
  if (typeof cents !== 'number') return null
  return `${currency} ${(cents / 100).toLocaleString()}`
}

// A value a cell can show as-is. Objects and nested arrays are not: dumping
// them as JSON gives a checker a wall of braces to read instead of an answer,
// so the fields inside them are declared individually in the tables below and
// anything still nested is left out of the comparison.
const isScalar = value =>
  value === null ||
  value === undefined ||
  typeof value === 'string' ||
  typeof value === 'number' ||
  typeof value === 'boolean'

// Turns a raw field value into the string shown in a cell. Both sides of a row
// go through this, so comparison and display never disagree.
const display = value => {
  if (value === null || value === undefined || value === '') return NO_VALUE
  if (typeof value === 'boolean') return yesNo(value)
  if (Array.isArray(value)) {
    const items = value.filter(isScalar).filter(item => item || item === 0)
    return items.length ? items.join(', ') : NO_VALUE
  }
  if (typeof value === 'object') return NO_VALUE
  return String(value)
}

// "res_district" -> "Res District", for additional_details keys nobody declared.
const humanise = key =>
  String(key)
    .split('_')
    .filter(Boolean)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')

// ---------------------------------------------------------------------------
// Field tables
// ---------------------------------------------------------------------------

const FIELD_GROUPS = [
  {
    title: 'Cover',
    fields: [
      { label: 'Product', read: q => q.product_name },
      { label: 'Package / Tier', read: q => details(q).tier },
      { label: 'Coverage Type', read: q => details(q).coverage_type },
      { label: 'Bus Cash Benefit', read: q => yesNo(details(q).bus_benefit) },
      { label: 'HospiCash Benefit', read: q => yesNo(details(q).hcp_benefit) },
      {
        label: 'Total Premium',
        read: (q, currency) =>
          asMoney(currency, q.total_premium_amount_in_cents)
      },
      { label: 'Cover Start Date', read: q => asDate(details(q).start_date) },
      {
        label: 'HospiCash Inception Date',
        read: q => asDate(details(q).hcp_inception_date)
      },
      {
        label: 'HospiCash Upgrade Date',
        read: q => asDate(details(q).hcp_upgrade_date)
      }
    ]
  },
  {
    title: 'Principal',
    fields: [
      { label: 'Name', read: q => getFullName(principalOf(q)) },
      { label: 'Title', read: q => details(q).title },
      { label: 'Initials', read: q => details(q).initials },
      { label: 'Maiden Name', read: q => details(q).maiden_name },
      {
        label: 'Date of Birth',
        // Also carried on additional_details, which is where it survives when a
        // quotation arrives with a bare schedule entry.
        read: q =>
          asDate(principalOf(q).date_of_birth || details(q).date_of_birth)
      },
      { label: 'Gender', read: q => principalOf(q).gender },
      {
        label: 'Phone Number',
        read: q => principalOf(q).msisdn || details(q).phone_number
      },
      {
        label: 'ID Number',
        read: q => principalOf(q).national_id || details(q).national_id
      },
      {
        label: 'Passport',
        read: q => principalOf(q).passport || details(q).passport
      },
      { label: 'Email', read: q => details(q).email },
      {
        label: 'Mobile Wallet Number',
        read: q => details(q).mobile_wallet_number
      },
      { label: 'Occupation', read: q => details(q).occupation },
      { label: 'Region', read: q => details(q).region },
      { label: 'District', read: q => details(q).res_district },
      { label: 'Village', read: q => details(q).res_village },
      {
        label: 'Traditional Authority',
        read: q => details(q).trad_authority
      },
      {
        label: 'Country of Residence',
        read: q => details(q).country_of_residence
      },
      {
        label: 'Politically Exposed',
        read: q => yesNo(details(q).is_politically_exposed)
      },
      {
        label: 'Related to Politically Exposed',
        read: q => yesNo(details(q).is_related_to_politically_exposed)
      }
    ]
  },
  {
    title: 'Payment',
    fields: [
      { label: 'Payment Method', read: q => details(q).payment_method },
      { label: 'Payment Day', read: q => details(q).payment_day },
      { label: 'Payment Frequency', read: q => details(q).payment_frequency },
      {
        label: 'Mobile Money Provider',
        read: q => mobileMoneyOf(q).provider
      },
      {
        label: 'Mobile Money Number',
        read: q => mobileMoneyOf(q).phone_number
      },
      {
        label: 'Premium Payer',
        read: q => {
          const value = details(q).premium_payer
          if (typeof value === 'string') {
            return value === 'principal' ? 'Principal (self)' : value
          }
          return value && typeof value === 'object' ? 'Someone else' : null
        }
      },
      { label: 'Payer Name', read: q => payerOf(q).payer_full_name },
      { label: 'Payer ID Number', read: q => payerOf(q).payer_id_number },
      {
        label: 'Payer Date of Birth',
        read: q => asDate(payerOf(q).payer_date_of_birth)
      },
      { label: 'Payer Gender', read: q => payerOf(q).payer_gender },
      { label: 'Payer Phone Number', read: q => payerOf(q).payer_phone_number },
      {
        label: 'Payer Relationship',
        read: q => payerOf(q).payer_relationship
      },
      {
        label: 'Payer Physical Address',
        read: q => payerOf(q).payer_physical_address
      }
    ]
  },
  {
    title: 'Beneficiary',
    fields: [
      {
        label: 'Name',
        read: q => getFullName(beneficiaryOf(q))
      },
      { label: 'Relationship', read: q => beneficiaryOf(q).relationship },
      { label: 'Phone Number', read: q => beneficiaryOf(q).msisdn },
      {
        label: 'Date of Birth',
        read: q => asDate(beneficiaryOf(q).date_of_birth)
      },
      { label: 'Gender', read: q => beneficiaryOf(q).gender },
      { label: 'ID Number', read: q => beneficiaryOf(q).national_id },
      { label: 'Passport', read: q => beneficiaryOf(q).passport }
    ]
  }
]

// additional_details keys the tables above already cover, plus the bookkeeping
// keys that change on every amendment and would only add noise. Anything else
// in additional_details is reported by diffRemainingDetails.
const COVERED_DETAIL_KEYS = [
  'tier',
  'coverage_type',
  'bus_benefit',
  'hcp_benefit',
  'hcp_inception_date',
  'hcp_upgrade_date',
  'hcp_declaration',
  'start_date',
  'title',
  'initials',
  'maiden_name',
  'email',
  'mobile_wallet_number',
  'occupation',
  'region',
  'res_district',
  'res_village',
  'trad_authority',
  'country_of_residence',
  'is_politically_exposed',
  'is_related_to_politically_exposed',
  'payment_method',
  'payment_day',
  'payment_frequency',
  'payment_info',
  'mobile_money',
  'premium_payer',
  'pending_swap',
  'payer_full_name',
  'payer_id_number',
  'payer_date_of_birth',
  'payer_gender',
  'payer_phone_number',
  'payer_relationship',
  'payer_physical_address',
  'beneficiary',
  'beneficiary_guid',
  'policy_number',
  'amended_at',
  'amendment_reason',
  // Read by the Principal fields above as a fallback for a bare schedule entry.
  'date_of_birth',
  'phone_number',
  'national_id',
  'passport'
]

// System identifiers and request plumbing — guids, flow run ids, correlation
// and trace ids. They change on every amendment and mean nothing to the person
// deciding it, so they never reach the comparison.
//
// Deliberately NOT a blanket /_id$/: that also matched national_id and
// passport_id, so a member's own ID document stored on additional_details
// (rather than on a schedule entry) was being filtered out silently. Only
// bookkeeping identifiers are named here; the value-shaped check below is the
// safety net for anything else that is really a guid.
const IDENTIFIER_KEY_PATTERN = /guid|uuid|flow_run|run_id|correlation|trace|idempotency|_token$|_key$|(^|_)(request|session|event|job|task|user|maker|checker)_ids?$/i

// Catches an identifier stored under a name the pattern above does not expect.
const GUID_VALUE_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

const looksLikeIdentifier = value =>
  typeof value === 'string' && GUID_VALUE_PATTERN.test(value.trim())

const MEMBER_FIELDS = [
  { label: 'Name', read: entry => getFullName(personOf(entry)) },
  { label: 'Relationship', read: entry => personOf(entry).relationship },
  {
    label: 'Date of Birth',
    read: entry => asDate(personOf(entry).date_of_birth)
  },
  { label: 'Gender', read: entry => personOf(entry).gender },
  { label: 'Phone Number', read: entry => personOf(entry).msisdn },
  { label: 'ID Number', read: entry => personOf(entry).national_id },
  {
    label: 'Premium',
    read: (entry, currency) => asMoney(currency, entry.premium_amount_in_cents)
  },
  {
    label: 'Waiting Period',
    read: entry => {
      const benefit = (entry.benefits || [])[0]
      if (!benefit || typeof benefit.waiting_period_in_days !== 'number') {
        return null
      }
      return `${benefit.waiting_period_in_days} days`
    }
  }
]

// ---------------------------------------------------------------------------
// Diffing
// ---------------------------------------------------------------------------

// A change needs two known values to compare. Quotations superseded by an
// amendment are far sparser than the amendment itself — a Taken_Up quotation
// may record only tier, coverage and the add-ons, with no name on its principal
// and no contact details at all. Reporting those as "— changed to Alice Banda"
// would tell the checker the member renamed themselves, when the truth is the
// old record never stored a name. So a row where the before side is unknown is
// withheld and counted, and only genuine before-and-after pairs are reported.
// Returns { rows, withheld } — the comparable differences, and a count of the
// fields the older record simply never stored.
const diffFields = (fields, before, after, currency) => {
  const rows = []
  let withheld = 0

  fields.forEach(field => {
    const was = display(field.read(before, currency))
    const now = display(field.read(after, currency))
    if (was === now) return
    if (was === NO_VALUE) {
      withheld += 1
      return
    }
    rows.push({ label: field.label, before: was, after: now })
  })

  return { rows, withheld }
}

// Catches additional_details changes the field tables do not declare, so a key
// added by a future amendment step still shows up instead of going silent.
// Identifiers and nested objects are held back: neither reads as an answer to
// "what changed", and the fields worth reading inside an object are declared.
const diffRemainingDetails = (before, after) => {
  const beforeDetails = details(before)
  const afterDetails = details(after)
  const keys = Object.keys(beforeDetails)
    .concat(Object.keys(afterDetails))
    .filter((key, index, all) => all.indexOf(key) === index)
    .filter(key => COVERED_DETAIL_KEYS.indexOf(key) === -1)
    .filter(key => !IDENTIFIER_KEY_PATTERN.test(key))
    .filter(key => isScalar(beforeDetails[key]) && isScalar(afterDetails[key]))
    .filter(
      key =>
        !looksLikeIdentifier(beforeDetails[key]) &&
        !looksLikeIdentifier(afterDetails[key])
    )
    .sort()

  const rows = []
  let withheld = 0

  keys.forEach(key => {
    const was = display(beforeDetails[key])
    const now = display(afterDetails[key])
    if (was === now) return
    if (was === NO_VALUE) {
      withheld += 1
      return
    }
    rows.push({ label: humanise(key), before: was, after: now })
  })

  return { rows, withheld }
}

// Members are matched on their ID document where one was captured, and on name
// plus date of birth otherwise, so a renamed member is not read as one member
// removed and another added.
const memberKey = entry => {
  const person = personOf(entry)
  const id = person.national_id || person.passport
  if (id)
    return `id:${String(id)
      .trim()
      .toLowerCase()}`

  // The name is the fallback identity, and only the name. Date of birth is
  // deliberately excluded: correcting a date of birth is itself a common
  // amendment, and folding it into the key would make that correction read as
  // one member removed and another added instead of one member edited. Two
  // same-named members on one policy would merge, which shows up as an edit
  // rather than an add plus a remove — the gentler of the two failures.
  //
  // A date of birth or phone number on its own is not an identity either.
  // Superseded entries routinely carry both with no name at all, and treating
  // that as an identity would block the relationship pairing below. Phone
  // numbers are never used: family members often share one.
  const name = [person.first_name, person.last_name]
    .filter(Boolean)
    .join('|')
    .trim()
  if (!name) return null

  return `name:${name.toLowerCase()}`
}

// A superseded quotation can hold an entry with no name and no ID at all — the
// old records carry only a relationship. Such an entry cannot be matched on
// identity, so it is paired by relationship when that relationship appears
// exactly once on each side. Without this the same person reads as one member
// removed and another added on every single amendment.
const relationshipKey = entry => {
  const relationship = personOf(entry).relationship
  return relationship ? `rel:${String(relationship).toLowerCase()}` : null
}

const uniqueRelationships = entries => {
  const counts = {}
  entries.forEach(entry => {
    const key = relationshipKey(entry)
    if (key) counts[key] = (counts[key] || 0) + 1
  })
  const unique = {}
  entries.forEach(entry => {
    const key = relationshipKey(entry)
    if (key && counts[key] === 1) unique[key] = entry
  })
  return unique
}

const memberLabel = (entry, currency) => {
  const person = personOf(entry)
  const name = getFullName(person) || 'Unnamed member'
  return {
    name,
    relationship: person.relationship || NO_VALUE,
    premium: display(asMoney(currency, entry.premium_amount_in_cents))
  }
}

const coverByType = entry => {
  const covers = {}
  ;(entry.benefits || []).forEach(benefit => {
    if (benefit && benefit.type) covers[benefit.type] = benefit
  })
  return covers
}

const diffBenefits = (before, after, currency) => {
  const beforeCovers = coverByType(before)
  const afterCovers = coverByType(after)
  return Object.keys(beforeCovers)
    .concat(Object.keys(afterCovers))
    .filter((type, index, all) => all.indexOf(type) === index)
    .sort()
    .map(type => {
      const was = beforeCovers[type]
      const now = afterCovers[type]
      return {
        label: type,
        before: display(
          was ? asMoney(currency, was.cover_amount_in_cents) : null
        ),
        after: display(
          now ? asMoney(currency, now.cover_amount_in_cents) : null
        )
      }
    })
    .filter(row => row.before !== row.after)
}

const diffMembers = (before, after, currency, principalLabels) => {
  const alreadyReported = principalLabels || {}
  const beforeList = entriesOf(before)
  const afterList = entriesOf(after)

  // A bucket per identity, not one entry per identity. Two same-named members
  // with no ID used to overwrite each other, and the loser vanished from the
  // added and removed lists both — the one failure mode nothing surfaced.
  // Matching consumes from the bucket, so every old entry is either paired or
  // ends up reported as removed.
  const byIdentity = {}
  const unidentified = []
  beforeList.forEach(entry => {
    const key = memberKey(entry)
    if (!key) {
      unidentified.push(entry)
      return
    }
    if (!byIdentity[key]) byIdentity[key] = []
    byIdentity[key].push(entry)
  })

  const afterByRelationship = uniqueRelationships(afterList)

  const added = []
  const changed = []
  let withheld = 0

  afterList.forEach(entry => {
    const key = memberKey(entry)
    const bucket = key ? byIdentity[key] : null
    let was = bucket && bucket.length ? bucket.shift() : null

    // No identity match: pair with an old entry that had no identity at all,
    // when the relationship is unambiguous on both sides.
    if (!was) {
      const relationship = relationshipKey(entry)
      if (relationship && afterByRelationship[relationship]) {
        const candidates = unidentified.filter(
          item => relationshipKey(item) === relationship
        )
        if (candidates.length === 1) {
          was = candidates[0]
          unidentified.splice(unidentified.indexOf(was), 1)
        }
      }
    }

    if (!was) {
      added.push(memberLabel(entry, currency))
      return
    }

    const fields = diffFields(MEMBER_FIELDS, was, entry, currency)
    withheld += fields.withheld

    // The Principal group above already reports the policy holder's own
    // details. Repeating them in their member table makes one change look like
    // two. Other members keep every row — a spouse's date of birth is not the
    // principal's.
    const memberRows = isPrincipal(entry)
      ? fields.rows.filter(row => !alreadyReported[row.label])
      : fields.rows

    const rows = memberRows.concat(diffBenefits(was, entry, currency))
    if (rows.length) {
      changed.push(Object.assign({ rows }, memberLabel(entry, currency)))
    }
  })

  // Whatever was never consumed above is genuinely gone from the amendment.
  const leftovers = Object.keys(byIdentity)
    .reduce((all, key) => all.concat(byIdentity[key]), [])
    .concat(unidentified)
  const removed = leftovers.map(entry => memberLabel(entry, currency))

  return { added, removed, changed, withheld }
}

// Compares two quotations and returns everything that differs.
// { groups, members, hasChanges, withheld } — withheld counts fields the older
// record never stored, which cannot honestly be reported as changes.
export const diffQuotations = (before, after, currency) => {
  const currencyCode = currency || ''
  let withheld = 0

  const groups = []
  FIELD_GROUPS.forEach(group => {
    const result = diffFields(group.fields, before, after, currencyCode)
    withheld += result.withheld
    if (result.rows.length) {
      groups.push({ title: group.title, rows: result.rows })
    }
  })

  const remaining = diffRemainingDetails(before, after)
  if (remaining.rows.length) {
    groups.push({ title: 'Other Details', rows: remaining.rows })
  }
  withheld += remaining.withheld

  const principalGroup = groups.filter(group => group.title === 'Principal')[0]
  const principalLabels = {}
  if (principalGroup) {
    principalGroup.rows.forEach(row => {
      principalLabels[row.label] = true
    })
  }

  const members = diffMembers(before, after, currencyCode, principalLabels)
  withheld += members.withheld

  const hasChanges =
    groups.length > 0 ||
    members.added.length > 0 ||
    members.removed.length > 0 ||
    members.changed.length > 0

  return { groups, members, hasChanges, withheld }
}

// ---------------------------------------------------------------------------
// Who this amendment is about
// ---------------------------------------------------------------------------

// Later sources win, but a blank never overwrites a value that was found. A
// quotation from the list endpoint can carry an empty principal, so a fuller
// record behind it (the policy's own quotation, or the customer) still shows.
const mergeDefined = sources => {
  const merged = {}
  sources.forEach(source => {
    if (!source || typeof source !== 'object') return
    Object.keys(source).forEach(key => {
      const value = source[key]
      if (value === null || value === undefined || value === '') return
      merged[key] = value
    })
  })
  return merged
}

const asRows = pairs =>
  pairs
    .map(pair => ({ label: pair[0], value: display(pair[1]) }))
    .filter(row => row.value !== NO_VALUE)

// The policy holder, assembled from every record that describes them: the
// customer, the quotation currently on the policy, and this amendment. Answers
// "who is this for" even when the amended quotation arrives without entries.
export const policyHolderDetails = (quote, context) => {
  const scope = context || {}
  const policyQuotation = scope.policy ? scope.policy.quotation : null
  const holder = mergeDefined([
    scope.customer,
    principalOf(policyQuotation),
    principalOf(quote)
  ])
  const ad = mergeDefined([details(policyQuotation), details(quote)])

  return asRows([
    ['Name', getFullName(holder)],
    ['Phone Number', holder.msisdn || holder.phone_number],
    ['ID Number', holder.national_id || holder.passport],
    ['Date of Birth', asDate(holder.date_of_birth)],
    ['Gender', holder.gender],
    ['Email', ad.email || holder.email]
  ])
}

// True when the premium is paid by somebody other than the policy holder.
//
// premium_payer is not a reliable flag on its own: it is absent on most
// policies, and where it is present it is an object whether the payer is the
// principal or not — sometimes marked 'Self', sometimes with every field blank.
// So the payer's name is what decides, and a name matching the principal's (or
// a relationship of Self) means the principal pays.
// 'self' and 'principal' both mean the policy holder pays. The string and object
// forms of premium_payer are held to the same rule — they used to disagree, so a
// flow writing the string 'Self' produced a third-party block on a self-paying
// policy.
const MEANS_POLICY_HOLDER = ['self', 'principal']

export const payerIsSomeoneElse = (quote, context) => {
  const value = details(quote).premium_payer
  if (typeof value === 'string') {
    return MEANS_POLICY_HOLDER.indexOf(value.trim().toLowerCase()) === -1
  }

  const payer = payerOf(quote)
  const name = payer.payer_full_name
  if (!name || !String(name).trim()) return false

  const relationship = String(payer.payer_relationship || '')
    .trim()
    .toLowerCase()
  if (MEANS_POLICY_HOLDER.indexOf(relationship) !== -1) return false

  const holder = getFullName(
    mergeDefined([context && context.customer, principalOf(quote)])
  )
  return normalise(name) !== normalise(holder)
}

// The third party paying the premium. Whoever approves an amendment needs to
// see who that is, whether or not this amendment is what changed them.
export const payerDetails = quote => {
  const payer = payerOf(quote)
  return asRows([
    ['Name', payer.payer_full_name],
    ['Relationship to Policy Holder', payer.payer_relationship],
    ['ID Number', payer.payer_id_number],
    ['Date of Birth', asDate(payer.payer_date_of_birth)],
    ['Gender', payer.payer_gender],
    ['Phone Number', payer.payer_phone_number],
    ['Physical Address', payer.payer_physical_address]
  ])
}

// ---------------------------------------------------------------------------
// Finding the quotation an amendment supersedes
// ---------------------------------------------------------------------------

const readPath = (source, path) =>
  path.reduce(
    (value, key) =>
      value && typeof value === 'object' ? value[key] : undefined,
    source
  )

export const getPreviousQuotationGuid = quote => {
  const ad = details(quote)
  for (let index = 0; index < PREVIOUS_GUID_PATHS.length; index += 1) {
    const value = readPath(ad, PREVIOUS_GUID_PATHS[index])
    if (typeof value === 'string' && value) return value
    if (value && typeof value === 'object' && value.guid) return value.guid
  }
  return null
}

// The policy this amendment belongs to. The quotation's own policy_guid stays
// empty until the swap is applied, so the pending swap is what names it.
export const getPolicyGuid = quote => {
  const swap = details(quote).pending_swap
  const fromSwap = swap && typeof swap === 'object' ? swap.policy_guid : null
  return quote.policy_guid || fromSwap || null
}

const unwrap = response => {
  const body = response && response.data ? response.data : null
  if (!body) return null
  return body.data && !Array.isArray(body.data) ? body.data : body
}

// These three reject on failure rather than swallowing it. fetchAmendmentContext
// is the single place that turns a rejection into a reported failure, so the
// panel can tell "the record names no predecessor" apart from "the request for
// it did not come back".
const fetchPolicy = quote => {
  const policyNumber = policyNumberOf(quote)
  if (!policyNumber) return Promise.resolve(null)

  return getPolicyProcessingDcp({
    policy_number: policyNumber
  }).then(response => (response && response.data ? response.data : null))
}

// The customer record is the fallback for who the policy is for. A quotation
// returned by the list endpoint may arrive without its schedule entries, and
// the principal's name and contact details must not go blank because of that.
// The policy stands in when the quotation carries no customer_guid of its own.
const fetchCustomer = (quote, policy) => {
  const guid =
    quote.customer_guid || (policy ? policy.customer_guid : null) || null
  if (!guid) return Promise.resolve(null)

  return getCustomerInfo(guid).then(response => unwrap(response))
}

// Last resort only. The quotation still attached to the policy is the state the
// amendment is asking to change, so it stands in when the pending swap does not
// name a predecessor.
const baselineFromPolicy = (quote, policy) => {
  const live = policy ? policy.quotation : null
  return live && live.guid !== quote.guid ? live : null
}

const fetchBaseline = quote => {
  const named = getPreviousQuotationGuid(quote)
  if (!named) return Promise.resolve(null)

  return getQuotationByGuid({ guid: named }).then(response => unwrap(response))
}

// Everything the panel needs about one amendment: the state it replaces, the
// policy it belongs to, and the customer behind it. In the normal case the
// quotation names both its predecessor and its customer, so this is a single
// round trip. The policy is only requested when one of those is missing —
// and it is requested alongside the baseline, not after it, so that its
// customer_guid can actually be used.
//
// Returns { previous, policy, customer, failed }; `failed` is true when a
// request errored, as opposed to there being nothing to find.
export const fetchAmendmentContext = quote => {
  const empty = { previous: null, policy: null, customer: null, failed: false }
  if (!quote) return Promise.resolve(empty)

  let failed = false
  const settle = promise =>
    promise.catch(() => {
      failed = true
      return null
    })

  const named = getPreviousQuotationGuid(quote)
  const needsPolicy = !named || !quote.customer_guid

  return Promise.all([
    settle(fetchBaseline(quote)),
    needsPolicy ? settle(fetchPolicy(quote)) : Promise.resolve(null)
  ]).then(resolved => {
    const found = resolved[0]
    const policy = resolved[1]

    return settle(fetchCustomer(quote, policy)).then(customer => ({
      previous: found || baselineFromPolicy(quote, policy),
      policy,
      customer,
      failed
    }))
  })
}
